// Speculo team members assigned as the "Client Owner" of an organization.
// The value of `Organization.client_owner` (a short identifier like "Ben" or
// "Leslie") is resolved through `getClientOwner` to a full profile so client
// support surfaces (Schedule Call, onboarding guide, crash-course banners,
// flow strategy CTAs, etc.) show the right person per org.
//
// When ownership shifts, edit the entry below — every UI surface reads from
// this file via getClientOwner / useClientOwner.

export interface ClientOwner {
  id: 'Ben' | 'Leslie';
  /** Short label used in pickers (first name only). */
  displayName: string;
  /** Full name used in greetings and avatars. */
  fullName: string;
  email: string;
  role: string;
  /** Path under /public/ — drop the avatar file here. */
  imageUrl: string;
  /** Base HubSpot meeting URL (append `?embed=true` for iframe embeds). */
  bookingUrl: string;
  /** Strategy-meeting URL used by Convert/Revive flow CTAs + crash course. */
  strategyMeetingUrl: string;
}

export const CLIENT_OWNERS: Record<ClientOwner['id'], ClientOwner> = {
  Ben: {
    id: 'Ben',
    displayName: 'Ben',
    fullName: 'Ben',
    email: 'ben@speculo.ai',
    role: 'Client Account Strategist',
    imageUrl: '/onboarding/BEN.webp',
    bookingUrl: 'https://meetings.hubspot.com/ben1778',
    strategyMeetingUrl: 'https://meetings.hubspot.com/ben1778/speculoai-strategy-meeting',
  },
  Leslie: {
    id: 'Leslie',
    displayName: 'Leslie',
    fullName: 'Leslie Rosales',
    email: 'leslie@speculo.ai',
    role: 'Client Account Strategist',
    imageUrl: '/onboarding/LESLIE.webp',
    bookingUrl: 'https://meetings.hubspot.com/leslie220',
    strategyMeetingUrl: 'https://meetings.hubspot.com/leslie220',
  },
};

/** Owner shown when the org has no `client_owner` set, or pre-auth surfaces. */
export const DEFAULT_CLIENT_OWNER_ID: ClientOwner['id'] = 'Ben';

/** Resolve a stored `client_owner` value to a full ClientOwner profile.
 *  Case-insensitive so legacy/migrated rows (e.g. "leslie", "BEN") still match. */
export const getClientOwner = (value: string | null | undefined): ClientOwner => {
  if (value) {
    const normalized = value.trim().toLowerCase();
    const match = Object.values(CLIENT_OWNERS).find(
      (o) => o.id.toLowerCase() === normalized,
    );
    if (match) return match;
  }
  return CLIENT_OWNERS[DEFAULT_CLIENT_OWNER_ID];
};

/** Picker options for Client Admin and similar selectors. */
export const CLIENT_OWNER_OPTIONS = Object.values(CLIENT_OWNERS).map((o) => ({
  value: o.id,
  label: o.displayName,
  email: o.email,
}));
